export const itemTemplate = ({
  label,
  count,
  isRefined,
  inputType = 'checkbox',
}) => `
  <label class="flex items-center rounded-sm px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer">
    <input
      type="${inputType}"
      class="input-check cursor-pointer"
      ${isRefined ? 'checked="true"' : ''}
    >
    <span class="ml-2 text-sm font-normal leading-5 text-gray-900 dark:text-gray-100">${label}
      <span class="ml-2 inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-700/20 dark:ring-gray-600/40">${count}</span>
    </span>
  </label>
`;

export const texts = {
  showMore: 'Show more',
  showLess: 'Show less',
  clearFilters: 'Clear filters',
  resultSingular: 'result',
  resultPlural: 'results',
  remove: 'Remove',
  all: 'All',
  searchPlaceholder: 'Search...',
  loading: 'Loading...',
  allResults: 'All results:',
  errorUnavailable: 'Search service is temporarily unavailable.',
  errorRateLimit: 'Search rate limit reached. Please try again in a moment.',
  errorAuth: 'Search service configuration error.',
  errorNetwork: 'Network error. Please check your connection.',
  errorConfig:
    'Algolia configuration missing. Search is temporarily unavailable.',
  untitled: 'Untitled',
};

export function setTexts(el) {
  if (!el?.dataset) return;
  Object.keys(texts).forEach((key) => {
    const attr = 'text' + key.charAt(0).toUpperCase() + key.slice(1);
    if (el.dataset[attr] !== undefined) {
      texts[key] = el.dataset[attr];
    }
  });
}

export const showMoreTemplate = ({ isShowingMore }) => `
  <button class="mt-2 text-sm text-gray-600 dark:text-gray-400 underline hover:text-gray-900 dark:hover:text-gray-100">
    ${isShowingMore ? texts.showLess : texts.showMore}
  </button>
`;

export const searchableTemplate = {
  searchableInput: `
    <input
      type="search"
      class="w-full mb-3 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
      placeholder="{{placeholder}}"
    >
  `,
  searchableSubmit: '',
  searchableReset: '',
};

export const commonClasses = {
  item: [],
  selectedItem: [],
  list: ['space-y-2'],
  noRefinementRoot: ['opacity-40', 'pointer-events-none'],
  showMore: ['mt-2'],
  count: ['text-xs', 'text-gray-600', 'dark:text-gray-400', 'ml-auto'],
};

export const panelClasses = {
  root: ['border-b', 'border-gray-200', 'dark:border-gray-700', 'py-3'],
  header: [
    'flex',
    'w-full',
    'flex-row',
    'justify-between',
    'rounded-sm',
    'px-2',
    'py-1',
    'mb-2',
    'hover:bg-gray-200',
    'dark:hover:bg-gray-700',
    'cursor-pointer',
  ],
  body: [],
  footer: [],
  collapsedRoot: ['s-collapsed'],
  collapseButton: ['cursor-pointer'],
  collapseIcon: [],
};

export const panelTemplates = {
  header: ({ widgetParams }) => `
    <div class="font-bold text-gray-900 dark:text-gray-100">
      ${widgetParams.label || widgetParams.attribute}
    </div>
  `,
  collapseButtonText: ({ collapsed }) => `
    <svg class="ml-1 transform text-gray-600 dark:text-gray-400 transition-transform ${collapsed ? '' : 'rotate-180'}" width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16.59 9.09L12 13.67L7.41 9.09L6 10.5L12 16.5L18 10.5L16.59 9.09Z" fill="currentColor"></path>
    </svg>
  `,
};

export const isCollapsed = (container) => {
  const el =
    typeof container === 'string'
      ? document.querySelector(container)
      : container;
  return el?.dataset.collapsed === 'true';
};

export const statsTemplate = ({ nbHits, hitsPerPage, page }) => {
  const start = page * hitsPerPage + 1;
  const end = Math.min((page + 1) * hitsPerPage, nbHits);
  return `
  <p class="text-sm text-gray-700 dark:text-gray-300">Showing <span class="font-semibold">${start}-${end}</span> of
    <span class="font-semibold">${nbHits.toLocaleString()}</span> ${nbHits === 1 ? texts.resultSingular : texts.resultPlural}</p>
  `;
};

export const clearRefinementsTemplate = {
  get resetLabel() {
    return 'Reset Search';
  },
};

export const paginationClasses = {
  root: ['mt-6'],
  list: ['isolate', 'inline-flex', 'rounded-md', '-space-x-px'],
  item: ['inline-flex'],
  link: [
    'relative',
    'inline-flex',
    'items-center',
    'px-4',
    'py-2',
    'text-sm',
    'text-gray-900',
    'dark:text-gray-100',
    'dark:bg-gray-900',
    'ring-1',
    'ring-inset',
    'ring-gray-300',
    'dark:ring-gray-700',
    'hover:bg-gray-200',
    'dark:hover:bg-gray-700',
    'cursor-pointer',
  ],
  selectedItem: [
    '[&_a]:bg-black',
    '[&_a]:text-white',
    '[&_a]:font-semibold',
    '[&_a]:cursor-default',
    '[&_a]:ring-0',
    '[&_a]:z-10',
    '[&_a]:hover:bg-black',
  ],
  disabledItem: ['!hidden'],
  firstPageItem: ['!hidden'],
  previousPageItem: ['[&_a]:rounded-l-md', '[&_a]:px-2'],
  pageItem: [],
  nextPageItem: ['[&_a]:rounded-r-md', '[&_a]:px-2'],
  lastPageItem: ['!hidden'],
};

export const currentRefinementsClasses = {
  root: [''],
  list: ['flex', 'flex-wrap', 'gap-2'],
  item: ['flex', 'items-center', 'text-sm'],
  label: ['text-gray-600', 'dark:text-gray-400', 'text-xs', 'mr-1', 'hidden'],
  category: [
    'inline-flex',
    'items-center',
    'gap-1',
    'rounded-full',
    'bg-gray-100',
    'dark:bg-gray-800',
    'px-3',
    'py-1',
    'text-sm',
    'font-medium',
    'text-gray-700',
    'dark:text-gray-300',
    'ring-1',
    'ring-inset',
    'ring-gray-700/20',
    'dark:ring-gray-600/40',
  ],
  delete: [
    'text-gray-600',
    'dark:text-gray-400',
    'hover:text-gray-800',
    'dark:hover:text-gray-200',
    'cursor-pointer',
    'transition-colors',
    'text-xs',
  ],
};
